var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { computed, observable } from 'mobx';
import { isArray } from '@cutwise/utils';
import { CustomGradingService } from '@/customGrading/services/CustomGradingService';
import GradeDictEntry from './GradeDictEntry';
export class GradeAttributeOption {
    code;
    spId;
    title;
}
export class GradeAttributeModel extends GradeAttributeOption {
    dict;
}
export default class GradeAttribute extends GradeAttributeModel {
    dict;
    basicDict;
    extraDict;
    // "dictMap" contains map of full dict array,
    // it includes "basicDict" and "extraDict"
    dictMap = {};
    spId;
    title;
    constructor({ code, dict, spId, title }) {
        super();
        this.code = code;
        this.dict = dict ? dict.map((d) => new GradeDictEntry(d)) : [];
        this.spId = spId;
        this.title = title;
        this.dictMap = this.getDictMap(this.dict);
        const [basicDict, extraDict] = this.getBasicDict();
        this.basicDict = basicDict;
        this.extraDict = extraDict;
    }
    get dictEntriesWithReferences() {
        return this.dict.filter((d) => !!d.refId);
    }
    get dictEntriesByReferenceMap() {
        return this.dictEntriesWithReferences.reduce((result, dictEntry) => {
            result[dictEntry.refId] = dictEntry;
            return result;
        }, {});
    }
    get referencesIds() {
        return this.dictEntriesWithReferences.map((d) => d.refId);
    }
    get maxDictEntryTitleLength() {
        let maxTitleLength = 2;
        this.dict.forEach((d) => {
            if (d.title.length > maxTitleLength) {
                maxTitleLength = d.title.length;
            }
        });
        return maxTitleLength;
    }
    get minDistanceBetweenReferences() {
        let reference = null;
        let minDistance = 3;
        let distance = 1;
        this.dict.forEach((d) => {
            if (reference === null) {
                if (!d.refId) {
                    return;
                }
                reference = d.refId;
                return;
            }
            if (!d.refId) {
                distance++;
                return;
            }
            if (d.refId && reference !== d.refId) {
                if (distance < minDistance) {
                    minDistance = distance;
                }
                distance = 1;
            }
        });
        return minDistance;
    }
    get maxGroupLength() {
        let length = 1;
        Object.keys(this.dictMap).forEach((title) => {
            const subgradesLength = this.dictMap[title].length;
            if (subgradesLength > length) {
                length = subgradesLength;
            }
        });
        return length;
    }
    getDictEntryById(id) {
        return this.dict?.find((d) => d.id === id) ?? null;
    }
    getManualBracketingValue(product) {
        return CustomGradingService.getManualBracketing(this.code, product, this.dict);
    }
    getSupplierGradeValue(product) {
        return CustomGradingService.getSupplierGrade(product, this.dict);
    }
    getCutwiseAutogradeValue(product, references) {
        return CustomGradingService.getCutwiseAutograde(this.code, product, references, this.dict);
    }
    getInitialGrade(product, references) {
        return CustomGradingService.getInitialGrade(this.code, product, references, this.dict, this.basicDict);
    }
    getDictMap(dict) {
        return dict.reduce((result, dictEntry) => {
            const lettersOnly = dictEntry.group;
            if (isArray(result[lettersOnly])) {
                result[lettersOnly].push(dictEntry);
            }
            else {
                result[lettersOnly] = [dictEntry];
            }
            result[lettersOnly].sort((a, b) => a.id - b.id);
            return result;
        }, {});
    }
    getBasicDict() {
        // copy array into new variable for immutability
        const fullDict = this.dict.slice();
        let lastDictEntryWithRefIndex = null;
        // iterate through array from last to first to find last element with reference
        for (let i = fullDict.length - 1; i >= 0; i--) {
            const dictEntry = fullDict[i];
            if (lastDictEntryWithRefIndex === null && dictEntry.refId) {
                // if grade is subgrade we need to find last grade in group
                // lets say we have group M- M M+ and only M- has reference, we need to find index of M+
                if (dictEntry.isSubgrade) {
                    const group = this.dictMap[dictEntry.group];
                    const lastInGroup = group[group.length - 1];
                    const targetIndex = fullDict.findIndex(({ id }) => id === lastInGroup.id);
                    if (targetIndex > -1) {
                        lastDictEntryWithRefIndex = targetIndex;
                        break;
                    }
                }
                else {
                    lastDictEntryWithRefIndex = i;
                    break;
                }
            }
        }
        if (lastDictEntryWithRefIndex === null) {
            return [fullDict, []];
        }
        // split initial array to basic (without grades line N, P, O, etc)
        const extraDict = fullDict.splice(lastDictEntryWithRefIndex + 1);
        return [fullDict, extraDict];
    }
}
__decorate([
    observable
], GradeAttribute.prototype, "dict", void 0);
__decorate([
    observable
], GradeAttribute.prototype, "basicDict", void 0);
__decorate([
    observable
], GradeAttribute.prototype, "extraDict", void 0);
__decorate([
    observable
], GradeAttribute.prototype, "dictMap", void 0);
__decorate([
    observable
], GradeAttribute.prototype, "spId", void 0);
__decorate([
    observable
], GradeAttribute.prototype, "title", void 0);
__decorate([
    computed
], GradeAttribute.prototype, "dictEntriesWithReferences", null);
__decorate([
    computed
], GradeAttribute.prototype, "dictEntriesByReferenceMap", null);
__decorate([
    computed
], GradeAttribute.prototype, "referencesIds", null);
__decorate([
    computed
], GradeAttribute.prototype, "maxDictEntryTitleLength", null);
__decorate([
    computed
], GradeAttribute.prototype, "minDistanceBetweenReferences", null);
__decorate([
    computed
], GradeAttribute.prototype, "maxGroupLength", null);
